import type { TickStatus } from '@/types'
import { CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react'

/**
 * Get urgency CSS class based on hours remaining until tick
 */
export function getUrgencyClass(hours: number): string {
  if (hours < 1) return 'text-red-500' // Critical
  if (hours < 3) return 'text-orange-500' // Warning
  if (hours < 6) return 'text-yellow-600' // Attention
  return 'text-muted-foreground' // Normal
}

/**
 * Configuration for tick status styling and icons
 */
export const statusConfig: Record<
  TickStatus,
  {
    icon: typeof CheckCircle2
    label: string
    tooltip: string
    containerClass: string
    iconClass: string
  }
> = {
  completed: {
    icon: CheckCircle2,
    label: 'Completed',
    tooltip: 'Last tick processed successfully',
    containerClass: 'bg-green-100 text-green-800 border-green-200',
    iconClass: 'text-green-600',
  },
  processing: {
    icon: Loader2,
    label: 'Processing',
    tooltip: 'Tick processing in progress...',
    containerClass: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClass: 'text-blue-600 animate-spin',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    tooltip: 'Last tick failed - admin notified',
    containerClass: 'bg-red-100 text-red-800 border-red-200',
    iconClass: 'text-red-600',
  },
  pending: {
    icon: Clock,
    label: 'Pending',
    tooltip: 'Awaiting processing',
    containerClass: 'bg-gray-100 text-gray-800 border-gray-200',
    iconClass: 'text-gray-600',
  },
}

/**
 * Game date representation
 */
export interface GameDate {
  year: number
  quarter: number
  month: number
}

/**
 * Convert tick number to in-game date.
 *
 * 1 tick = 3 in-game months
 */
export function tickToGameDate(tick: number): GameDate {
  const monthsElapsed = tick * 3
  const year = Math.floor(monthsElapsed / 12) + 1
  const month = (monthsElapsed % 12) + 1
  const quarter = Math.ceil(month / 3)

  return { year, quarter, month }
}
